// Slice 32 — Oracle rename emitter.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 32.
//
// Emits dialect-native ALTER syntax for body rewrites + table/view rename.
// The planner (renamePlanner.ts) already routes column rename schema-ALTERs
// through `DdlStructuralEdit.applyDdlEdit`; this file owns table/view rename
// + object body rewrites only.
//
// Oracle is in the PG family — view / procedure / function / trigger body
// rewrites use `CREATE OR REPLACE`. Tables rename via `ALTER TABLE … RENAME
// TO`; views via the `RENAME` statement (same-schema only — Oracle's `RENAME`
// has no schema-qualified target form, so cross-schema view rename falls back
// to DROP + CREATE).
//
// Body shapes (as returned by the introspection feed in `oracleObjectBodyFetcher.ts`):
//   - view → bare SELECT body (`ALL_VIEWS.TEXT`).
//   - scalar-function / inline-tvf → `ALL_SOURCE` rows reassembled. Oracle
//     stores the source WITHOUT the leading `CREATE [OR REPLACE]` keyword —
//     the body starts with `FUNCTION name(...)`. `ensureCreateOrReplace`
//     handles all three shapes (no prefix, plain `CREATE`, `CREATE OR REPLACE`).
//   - default-constraint → bare expression text (`ALL_TAB_COLUMNS.DATA_DEFAULT`),
//     synthesized object name is `<table>.<column>`.
//   - check-constraint → `SEARCH_CONDITION` text (the bare boolean expression
//     between the `CHECK (` and the closing `)`); the parent table lives in
//     `ALL_CONSTRAINTS.TABLE_NAME` but is not threaded through the index, so
//     we resolve it at runtime via PL/SQL `EXECUTE IMMEDIATE`.
//
// Quoting: Oracle identifiers are double-quoted; unquoted identifiers fold to
// UPPERCASE in the catalog. We always emit double-quoted form so the schema
// + name strings travel through the catalog verbatim regardless of casing.

import type { ObjectKind, QualifiedName } from '../../types';

import type {
  DialectRenameEmitter,
  EmitObjectBodyAlterInput,
} from './types';

const DEFAULT_SCHEMA = '';

function quoteIdent(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}

function quoteSqlLiteral(text: string): string {
  return text.replace(/'/g, "''");
}

function schemaOf(qn: QualifiedName): string {
  return qn.schema && qn.schema.length > 0 ? qn.schema : DEFAULT_SCHEMA;
}

function quotedQualified(qn: QualifiedName): string {
  const schema = schemaOf(qn);
  return schema ? `${quoteIdent(schema)}.${quoteIdent(qn.name)}` : quoteIdent(qn.name);
}

/** Substitute or prepend `CREATE OR REPLACE` so the body is a self-contained
 *  CREATE-form statement Oracle's parser accepts. Three input shapes:
 *    1. `CREATE OR REPLACE FUNCTION …` — already correct, returned verbatim.
 *    2. `CREATE FUNCTION …`             — substituted to `CREATE OR REPLACE`.
 *    3. `FUNCTION …` (the ALL_SOURCE shape — no leading CREATE)
 *                                       — `CREATE OR REPLACE ` prepended.
 *  Whitespace + casing of the rest is preserved byte-for-byte so any
 *  modifiers (RETURN type, signature, AUTHID, IS/AS body) survive intact. */
function ensureCreateOrReplace(body: string): string {
  const stripped = body.replace(/^\s*/, '');
  if (/^create\s+or\s+replace\s+/i.test(stripped)) return stripped;
  if (/^create\s+/i.test(stripped)) {
    return stripped.replace(/^create\s+/i, 'CREATE OR REPLACE ');
  }
  return `CREATE OR REPLACE ${stripped}`;
}

/** Wrap a `CREATE OR REPLACE FUNCTION/PROCEDURE/TRIGGER … END;` DDL in
 *  `BEGIN EXECUTE IMMEDIATE q'[…]'; END;` so it survives the Oracle
 *  statement splitter intact.
 *
 *  Why: the renderer's `splitOracleStatements` consumes the `;` after the
 *  module's final `END` as a top-level statement separator, leaving the
 *  captured DDL ending in bare `END`. Oracle's CREATE-FUNCTION compiler
 *  rejects that with PLS-00103 ("Encountered end-of-file when expecting `;`")
 *  and the function lands in INVALID state. Wrapping in an anonymous PL/SQL
 *  block keeps the inner DDL inside a string literal — the splitter sees one
 *  outer block whose `END;` is the only top-level boundary, and the
 *  Oracle adapter's `isPlsqlBlock` check then re-appends the outer `;` so
 *  oracledb accepts the block.
 *
 *  We use Oracle's `q'[…]'` quoting so single quotes inside the body don't
 *  need escaping. The opening delimiter is `q'[` and the closing is `]'`
 *  (the SINGLE matched-character form — a hypothetical `q'<jamsql>…</jamsql>'`
 *  shape doesn't work because Oracle's q-quote delimiter is a single char,
 *  not a multi-char tag). Bracketed PL/SQL bodies almost never contain `]'`,
 *  so this is robust in practice.
 *
 *  EXECUTE IMMEDIATE for CREATE FUNCTION/PROCEDURE/TRIGGER REQUIRES the
 *  trailing `;` after the inner PL/SQL body's `END` (because the body itself
 *  is PL/SQL). We ensure the inner DDL ends with exactly one `END;`. */
function wrapInExecuteImmediate(ddl: string): string {
  const trimmed = ddl.trimEnd();
  const ddlWithSemicolon = trimmed.endsWith(';') ? trimmed : `${trimmed};`;
  return [
    'BEGIN',
    `  EXECUTE IMMEDIATE q'[${ddlWithSemicolon}]';`,
    'END;',
  ].join('\n');
}

function emitViewBodyAlter(qn: QualifiedName, newBody: string): string {
  // `ALL_VIEWS.TEXT` returns the bare SELECT — re-wrap in CREATE OR REPLACE
  // VIEW. Trailing `;` is stripped so the result has no double terminator.
  const inner = newBody.replace(/;\s*$/, '').trim();
  return `CREATE OR REPLACE VIEW ${quotedQualified(qn)} AS ${inner}`;
}

function emitFunctionBodyAlter(newBody: string): string {
  return wrapInExecuteImmediate(ensureCreateOrReplace(newBody));
}

function emitTriggerBodyAlter(newBody: string): string {
  return wrapInExecuteImmediate(ensureCreateOrReplace(newBody));
}

function emitDefaultConstraintAlter(
  qn: QualifiedName,
  newExpr: string,
): string {
  // Synthesized name is `<table>.<column>` — split on the LAST dot.
  const dot = qn.name.lastIndexOf('.');
  if (dot < 0) {
    return `-- TODO(rename emit): default-constraint name "${qn.name}" missing the synthesized "<table>.<column>" shape; cannot emit ALTER`;
  }
  const table = qn.name.slice(0, dot);
  const column = qn.name.slice(dot + 1);
  const schema = schemaOf(qn);
  const tableRef = schema ? `${quoteIdent(schema)}.${quoteIdent(table)}` : quoteIdent(table);
  return `ALTER TABLE ${tableRef} MODIFY (${quoteIdent(column)} DEFAULT ${newExpr.trim()})`;
}

function emitCheckConstraintAlter(
  qn: QualifiedName,
  newBody: string,
): string {
  // `SEARCH_CONDITION` carries the bare boolean expression (no `CHECK (` /
  // `)` wrapper); the planner's input may be either bare or already wrapped
  // in `CHECK (…)`. Strip the CHECK prefix so we re-wrap consistently.
  const schema = schemaOf(qn);
  const sLit = quoteSqlLiteral(schema);
  const nLit = quoteSqlLiteral(qn.name);
  const quotedName = quoteIdent(qn.name);
  const trimmed = newBody.trim();
  const checkRe = /^check\s*\(([\s\S]*)\)\s*$/i;
  const match = checkRe.exec(trimmed);
  const innerExpr = match ? match[1].trim() : trimmed;
  // Embed inside `EXECUTE IMMEDIATE 'ALTER TABLE … '` so every `'` in the
  // doubled-quoted name + the expression must be doubled for the outer
  // PL/SQL string literal.
  const innerExprLit = quoteSqlLiteral(innerExpr);
  const quotedNameLit = quoteSqlLiteral(quotedName);
  const schemaFilter = schema
    ? `AND OWNER = '${sLit}'`
    : '';
  return [
    'DECLARE',
    '  v_table VARCHAR2(258);',
    '  v_owner VARCHAR2(128);',
    'BEGIN',
    '  SELECT OWNER, TABLE_NAME INTO v_owner, v_table',
    '    FROM ALL_CONSTRAINTS',
    `    WHERE CONSTRAINT_NAME = '${nLit}' ${schemaFilter} AND CONSTRAINT_TYPE = 'C'`,
    '    FETCH FIRST 1 ROWS ONLY;',
    `  EXECUTE IMMEDIATE 'ALTER TABLE "' || v_owner || '"."' || v_table || '" DROP CONSTRAINT ${quotedNameLit}';`,
    `  EXECUTE IMMEDIATE 'ALTER TABLE "' || v_owner || '"."' || v_table || '" ADD CONSTRAINT ${quotedNameLit} CHECK (${innerExprLit})';`,
    'END;',
  ].join('\n');
}

function isModuleKind(kind: ObjectKind): boolean {
  return (
    kind === 'view' ||
    kind === 'materialized-view' ||
    kind === 'inline-tvf' ||
    kind === 'scalar-function' ||
    kind === 'procedure' ||
    kind === 'trigger'
  );
}

export function emitObjectBodyAlter(input: EmitObjectBodyAlterInput): string {
  const { objectKind, objectName, newBody } = input;
  if (objectKind === 'check-constraint') {
    return emitCheckConstraintAlter(objectName, newBody);
  }
  if (objectKind === 'default-constraint') {
    return emitDefaultConstraintAlter(objectName, newBody);
  }
  if (objectKind === 'view' || objectKind === 'materialized-view') {
    return emitViewBodyAlter(objectName, newBody);
  }
  if (objectKind === 'trigger') {
    return emitTriggerBodyAlter(newBody);
  }
  if (isModuleKind(objectKind)) {
    return emitFunctionBodyAlter(newBody);
  }
  return emitFunctionBodyAlter(newBody);
}

export type SchemaRenameInput = {
  readonly kind: 'table' | 'view';
  readonly from: QualifiedName;
  readonly to: QualifiedName;
};

export function emitTableOrViewSchemaAlter(input: SchemaRenameInput): string {
  if (input.kind === 'table') {
    // Oracle accepts both `ALTER TABLE sch.old RENAME TO new` and the
    // schema-less `RENAME old TO new`; the ALTER form survives qualified
    // identifiers, so we always emit it. The TO name is unqualified (Oracle
    // forbids schema-qualifying the target — the new name lives in the same
    // schema as the source).
    return `ALTER TABLE ${quotedQualified(input.from)} RENAME TO ${quoteIdent(input.to.name)}`;
  }
  // Views (NAV-2): Oracle has no `ALTER VIEW … RENAME TO`, BUT the standalone
  // `RENAME old TO new` statement renames any object (table OR view) in the
  // current schema non-destructively — it preserves the view's real
  // definition. This replaces the old data-destroying `CREATE OR REPLACE VIEW
  // new AS SELECT * FROM old; DROP VIEW old;` placeholder, which both broke
  // the new view and irrecoverably lost the original SELECT body.
  //
  // Oracle's `RENAME` does not accept a schema-qualified source or target —
  // it operates on the current schema's namespace only (same constraint that
  // already applied to the prior placeholder's same-schema assumption). We
  // emit bare-name source + target accordingly.
  return `RENAME ${quoteIdent(input.from.name)} TO ${quoteIdent(input.to.name)}`;
}

export type EmitUndoAlterInput =
  | {
      readonly kind: 'body';
      readonly objectKind: ObjectKind;
      readonly objectName: QualifiedName;
      readonly originalBody: string;
    }
  | {
      readonly kind: 'schema';
      readonly schemaRename: SchemaRenameInput;
    };

export function emitUndoAlter(input: EmitUndoAlterInput): string {
  if (input.kind === 'body') {
    return emitObjectBodyAlter({
      objectKind: input.objectKind,
      objectName: input.objectName,
      newBody: input.originalBody,
    });
  }
  return emitTableOrViewSchemaAlter({
    kind: input.schemaRename.kind,
    from: input.schemaRename.to,
    to: input.schemaRename.from,
  });
}

export const oracleRenameEmitter: DialectRenameEmitter = {
  emitObjectBodyAlter,
  emitTableOrViewSchemaAlter,
};
